import uniqid from 'uniqid';
import ReactiveComponent, { computed, watch } from '@utils/reactive-component';
import priceString from '@utils/price-string';
import { duplicateValuesByProp } from '@utils/filters';
import template, { selectBox, selectLabel, selectOption, promoLegend } from './templates/pdp-quickshop';
import PdpAddToCart from './pdp-add-to-cart';
import PdpLogin from './pdp-login';
import AjaxLoader from '../ajax-loader/ajax-loader';
import PdpSlider from './pdp-slider';
import QuantitySelector from '../quantity-selector/quantity-selector';
import { defaults } from './pdp-mappings';
import { exceptionBehaviorValues } from '@stores/cart/cart-store';
import * as productDatalayer from '@datalayer/components/product-datalayer';
import * as productDetailsDatalayer from '@datalayer/components/product-details-datalayer';
import catalogProduct from '@api/routes/quickview/quickview';

/* const mapPromos = (variant) => {
    const promoModels = variant.PromotionModels ?  [ ...variant.PromotionModels ] : [];
    const exception = variant.ProductException ?  { ...variant.ProductException } : null;

    if(exception && exception.Title) {
        promoModels.push({
            Id: uniqid.time('', '_exceptionPromo_'),
            Name: exception.Title,
            Description: exception.Description
        });
    }

    return promoModels || [];
}; */

class PdpSelector extends ReactiveComponent {
    #cachedProduct = {};
    #isPopState = false;

    constructor({ $el, dataL, data }) {
        super();

        const parsed = $.parseHTML(template);
        $el.append(parsed);

        this.$el = $el;

        // non-dynamic label regions
        this.$priceLabel = this.$el.find('[data-label="price"]');
        this.$quantityLabel = this.$el.find('[data-label="quantity"]');
        this.$rxLabel = this.$el.find('[data-label="prescription"]');
        this.$videoLabel = this.$el.find('[data-label="video"]');

        // placeholders for other components
        this.$addtoCartPlaceholder = this.$el.find('[data-placeholder="cart"]')
        this.$infoTabsPlaceholder = this.$el.find('[data-placeholder="infoTabs"]')
        this.$loginPlaceholder = this.$el.find('[data-placeholder="login"]');
        this.$quantityPlaceholder = this.$el.find('[data-placeholder="quantity"]');
        this.$sliderPlaceholder = this.$el.find('[data-placeholder="slider"]');
        this.$selectorPlaceholder = this.$el.find('[data-placeholder="selectors"]');

        // dynamic data regions
        this.$benefits = this.$el.find('[data-region="benefits"]');
        this.$brandName = this.$el.find('[data-region="brandName"]');
        this.$price = this.$el.find('[data-region="price"]');
        this.$productName = this.$el.find('[data-region="productName"]');
        this.$rx = this.$el.find('[data-region="prescription"]');
        this.$rxStamp = this.$el.find('[data-region="prescriptionStamp"]');
        this.$discountPrice = this.$el.find('[data-region="discountPrice"]');
        this.$listPrice = this.$el.find('[data-region="listPrice"]');
        this.$video = this.$el.find('[data-region="video"]');
        this.$videoIframe = this.$el.find('[data-region="videoIframe"]');
        this.$promoLegend = this.$el.find('[data-placeholder="promoLegend"]');

        this.$loaderPlaceholder = this.$el.find('[data-placeholder="loader"]');

        this.loader = null;
        
        // deferred dynamic data regions
        this.$selectorLabels = null; // assigned after selectors are rendered
        this.$selectors = null; // assigned after selectors are rendered

        // accessibility regions
        this.$ariaLive = this.$el.find('[data-a11y="ariaLive"]');

        // ui controls
        this.$quantityControl = this.$el.find('[data-control="quantity"]');
        this.$selectorBoxes = null; // assigned after selectors are rendered

        // ui components
        this.addToCart = null;
        this.quantitySelector = null;
        this.login = null;
        this.slider = null;
        this.tabs = null;

        // merge data from backend w/ fe defaults so there is always a value
        this.labels = 
        dataL.Labels ? { 
                ...defaults.labels,
                ...dataL.Labels 
            } : defaults.labels;

        // labels
        this.pleaseSelect = this.labels.PleaseSelect;

        // data points used for selector logic
        this.isAuthenticated = data?.UserAuthenticated || false;
        this.product = data?.Product || {};
        this.variants = data?.ChildVariants || [];
        this.selectorOptions = data?.VariantSelectorOptions || [];

        // filter out duplicate select values
        this.uiSelectorOptions = this.removeDuplicateSelectorValues();

        // setup pre-selected selectors 
        this.initialSelectors = this.setupInitialSelectors();



        this.currencyCode = this.product.CurrencyCode || 'USD'; // todo: BE to add currency code to data

        // state props
        this.reactive({
            /**
             * matrix of key/value pairs representing active selectors
             * - key is the selector property string
             * - value is the selector value string
             * @type {Array<Array<string>>}
             */
            selectors: this.setupSelectors(),
            /**
             * array of possible variants based on active selectors
             * @type {Array<Object<string,any>>}
             */
            filteredProducts: this.variants
        });

        // filter out duplicate promos for legend
        this.promos = this.variants.map(this.mapPromos, this).flat() || [];
        this.uiPromos = this.removeDuplicatePromoValues();


        this.init();
    }

    [computed]() {
        return {
            activeProduct() {
                return this.isVariant ? this.filteredProducts[0] : this.product;
            },
            activeGAProduct() {
                const exceptionBehavior = this.activeProduct?.ProductException?.PriceTreatment ?? exceptionBehaviorValues.NO_EXCEPTION;

                return productDatalayer.buildProduct({
                    id: this.activeProduct.ProductId,
                    variantId: this.activeProduct.VariantId,
                    name: this.activeProduct.Name,
                    brand: this.activeProduct.Brand,
                    category: this.product.CategoryList,
                    price: this.isVariant ? this.activeProduct.ListPrice : undefined,
                    discount: exceptionBehavior !== exceptionBehaviorValues.NO_EXCEPTION,
                    rxRequired: this.activeProduct.RxRequired
                });
            },
            activeSelectors() {
                return this.selectors.filter(([, value ]) => value);
            },
            isVariant() {
                return this.selectors.length === this.activeSelectors.length;
            },
            slides() {
                return this.activeProduct.Images?.map(image => ({
                    url: image.Url || image.ConstrainedUrl,
                    thumbnail: image.ConstrainedUrl || image.Url,
                    alt: image.Alt || this.product.DisplayName
                }));
            }
        }
    }

    [watch]() {
        return {
            isVariant() {
                if(this.#cachedProduct.VariantId !== this.activeProduct.VariantId) {
                    this.sendProductViewDatalayer();
                }

                this.tabs?.update({
                    productInformation: this.activeProduct.ProductInformation,
                    ingredients: this.activeProduct.Ingredients,
                    species: this.activeProduct.ForSpecies,
                    usage: this.activeProduct.Usage
                });

                this.toggleVideo();
            },
                        filteredProducts() {
                this.promos = this.filteredProducts.map(this.mapPromos, this).flat() || [];
                this.uiPromos = this.removeDuplicatePromoValues();
            },
            selectors() {
                this.updateState();
                this.isPopState();
            }
        }
    }

    init() {
        // render all markup that will never be removed from the DOM
        this.renderLoader();
        this.renderLabels();
        this.renderStaticRegions();
        this.renderAddToCart();
        this.renderQuantitySelector();
        this.renderSelectors();
        this.updateRenderedOptions();
        this.resetRadioInputs();
        this.renderSlider();
        this.renderPromoLegend();

        // setup initial accessibility state
        this.setupAccessibility();

        // setup ui state
        this.setupListeners();

        // setup popstate history state object
        this.setupHistoryState();

        // update state based off of any initial selectors
        this.updateState();

        this.sendProductViewDatalayer();
    }

    filterProducts(products = this.variants, selectors = this.selectors) {
        if(!products.length || !selectors.length) {
            return products;
        }

        let filteredProducts = [ ...products ];
        let index = 0;
        let value = '';

        do {
            const [ selectorProp, selectorFormattedValue ] = selectors[index];
            // get the "Value" prop which will be the true value to compare to
            value = this.uiSelectorOptions[index]?.Values.find((option) => option.FormattedValue === selectorFormattedValue)?.Value || null;
            if(value) {
                filteredProducts = filteredProducts.filter(product => product[selectorProp] === value);
            }
            index++;
        } while (value && filteredProducts.length > 1 && index < selectors.length);
        return filteredProducts;
    }

    filterOptions(index, filteredProducts = this.filteredProducts) {
        index = Number(index);
        if(isNaN(index) || !filteredProducts || !Array.isArray(filteredProducts)) {
            return [];
        }

        return this.uiSelectorOptions[index]?.Values.filter(option => {
                    const selectorProp = this.uiSelectorOptions[index].Name;
                    return filteredProducts.find(product => product[selectorProp] === option.Value);
                }) || [];
    }

    getSelectorsFromUrl() {
        const selectors = {};
         const urlParams = new URLSearchParams(window.location.search);

       /* for (const selector of this.uiSelectorOptions) {
            const key = selector.Name;
            const value = urlParams.get(key);
            const option = selector.Values?.find((option) => option.FormattedValue === value);

            if(option) {
                selectors[key] = value;
            } else {
                break;
            }
        } */

        return selectors;
    }

    onPopState() {
        this.#isPopState = true;

        this.initialSelectors = this.getSelectorsFromUrl();
        this.selectors = this.setupSelectors();

        if(this.isVariant) {
            this.updateRenderedOptions();
        }

        this.#isPopState = false;
    }

    onSelectorChange(evt) {
        const $target = $(evt.target);
        const currentIndex = $target.data('index');
        const key = $target.attr('name');
        const value = $target.val();

        this.#cachedProduct = this.activeProduct;

        let selectors = this.selectors.map((selector, index) => {
            const [ key ] = selector;
            return index > currentIndex ? [ key, '' ] : selector;
        });

        selectors[currentIndex] = [
            key,
            value
        ];

        this.selectors = this.resetSelectors(selectors, currentIndex + 1);
    }

    isPopState() {
        if(!this.#isPopState) {
            const urlParams = new URLSearchParams(this.activeSelectors);
            const queryStr = urlParams.toString();
            const url = `${window.location.pathname}?${queryStr}`;
            //window.history.pushState(null, '', url);
            this.login?.setLink();
        }
    }

    removeDuplicateSelectorValues() {
        return this.selectorOptions
            .map(option => {
                const values = duplicateValuesByProp(option.Values, 'Value');
                values.sort((first, second) => first.SortOrder - second.SortOrder);

                return { ...option, Values: values };
            });
    }

    removeDuplicatePromoValues() {
        if (!Array.isArray(this.promos) || typeof this.promos[0] !== 'object') {
            return [];
        }
        const values = duplicateValuesByProp(this.promos, 'Id');
        return { values };
    }

    mapPromos(variant){
        const promoModels = variant.PromotionModels ?  [ ...variant.PromotionModels ] : [];
        if(this.isVariant){
            const exception = variant.ProductException ?  { ...variant.ProductException } : null;
            if(exception && exception.Title) {
                promoModels.push({
                    Id: uniqid.time('', '_exceptionPromo_'),
                    Name: exception.Title,
                    Description: exception.Description
                });
            }
        }
        return promoModels || [];
    };

    renderAddToCart() {
        this.addToCart = new PdpAddToCart(this.$addtoCartPlaceholder, this.labels, {
            catalogName: this.product.CatalogName,
            displayName: this.product.DisplayName,
            exceptionAmount: this.product?.ProductException?.DiscountAmount,
            exceptionBehavior: this.product?.ProductException?.PriceTreatment,
            backOrdered: false,
            productId: this.product.ProductId
        });

        const setVariant = () => {
            this.addToCart.setVariant({
                exceptionAmount: this.activeProduct?.ProductException?.DiscountAmount,
                exceptionBehavior: this.activeProduct?.ProductException?.PriceTreatment,
                variantId: this.activeProduct.VariantId ?? null,
                backOrdered: this.activeProduct?.BackOrdered
            });
        }

        setVariant();
        this.watch('isVariant', () => setVariant());
    }

    renderLoader() {
        this.loader = new AjaxLoader(this.$loaderPlaceholder, { size: 'xs' });
        this.loader.render();
    }

    renderLabels() {
        this.$priceLabel.html(this.labels.YourPrice);
        this.$quantityLabel.html(this.labels.Quantity);
        this.$rxLabel.html(this.labels.RxText);
        this.$videoLabel.html(this.labels.HowTo);
        this.$productName.attr('href',`/shopmyvca/product/${this.product.ProductId}`);
    }

    renderBenefits() {
        this.$benefits.html(this.activeProduct.Benefits);
    }

    renderQuantitySelector() {
        // need to map labels since quantity selector will be used generically around the site
        const labels = {
            decrement: this.labels.Decrement,
            increment: this.labels.Increment,
            inputAriaLabel: this.labels.QuantitySelector
        };

        this.quantitySelector = new QuantitySelector(this.$quantityPlaceholder, {}, labels);

        this.quantitySelector.watchValue(value => {
            this.addToCart.setQuantity(value);
        });
    }

    renderNextOption(index, options = []) {
        this.renderSelectorOptions(this.$selectorBoxes.eq(index), options);

        // show the next selector
        this.$selectorLabels.eq(index).show();
        this.$selectors.eq(index).show();

        // hide any beyond the next one to be selected
        this.$selectorLabels.slice(index + 1).hide();
        this.$selectors.slice(index + 1).hide();
    }

    renderSelectors() {
        let selectorHtmlStr = '';

        this.uiSelectorOptions.forEach((option, index) => {
            // generate markup string for each selector
            const args = {
                id: `pdp${option.Name}`,
                index,
                label: option.DisplayName || option.Name,
                name: option.Name
            };
            const label = selectLabel(args);
            const selector = selectBox(args);

            selectorHtmlStr += label + selector;
        });

        this.$selectorPlaceholder.replaceWith(
            $.parseHTML(selectorHtmlStr)
        );

        // assign newly rendered ui elements
        this.$selectorBoxes = this.$el.find('[data-control="selector"]');
        this.$selectors = this.$el.find('[data-region="selector"]');
        this.$selectorLabels = this.$el.find('[data-region="selectorLabel"]');
    }

    renderSelectorOptions($selectEl, optionsArr = []) {
        let options = selectOption({ disabled: true, value: '', name: this.pleaseSelect });

        optionsArr.forEach(option => {
            options += selectOption({ value: option.FormattedValue || option.Value, name: option.Value });
        });

        $selectEl.html(
            $.parseHTML(options)
        );
    }

    renderSlider() {
        this.slider = new PdpSlider({
            $el: this.$sliderPlaceholder,
            labels: this.labels,
            rx: this.activeProduct.RxRequired,
            slides: this.slides,
            useTemplate: true
        });
    }

    renderPromoLegend() {
        let promoHtmlStr = [];
        if (this.uiPromos.length !== 0) {
            this.uiPromos.values.forEach((item, index) => {
                const args = {
                    id: item.Id,
                    index,
                    name: item.Name,
                    desc: item.Description,
                    iconURL: this.labels.PromoIcon
                };

                const promo = promoLegend(args);
                promoHtmlStr += promo;
            });

            this.$promoLegend.html(
                $.parseHTML(promoHtmlStr)
            );

            window.VCA.Tooltips.initVCAPromoTooltip();
        };
    }

    renderStaticRegions() {
        this.$productName.append(this.product.DisplayName);
        this.$brandName.append(this.product.Brand);
        //this.renderBenefits();
        this.toggleVideo();
    }

    renderRadioInputs() {
        let isAutoShipOnly = this.activeProduct.AutoShipOnly;
        let radioPurchaseElement = document.querySelector('.pdp-info__radio.-purchase');
        let radioSubscribeElement = document.querySelector('.pdp-info__radio #purchaseType1');

        radioPurchaseElement.style.display = 'block';

        if (isAutoShipOnly) {
            radioPurchaseElement.style.display = 'none';
            radioSubscribeElement.checked = true;
        }
    }

    resetRadioInputs() {
        let defaultRadioBtn = document.querySelector('.pdp-info__radio.-subscribe');

        if (defaultRadioBtn !== undefined || defaultRadioBtn !== null) {
            defaultRadioBtn.querySelector('input[type="radio"]').click();
        }
    }

    resetSelectors(selectorsArg = this.selectors, nextIndex = 0) {
        let selectors = [ ...selectorsArg ];

        for(nextIndex; nextIndex < selectors.length; nextIndex++) {
            const filteredProducts = this.filterProducts(this.variants, selectors);
            const filteredOptions = this.filterOptions(nextIndex, filteredProducts);

            // automatically select next option if there is only one value
            if(filteredOptions.length === 1) {
                const [ nextKey ] = selectors[nextIndex];
                const { FormattedValue: nextValue } = filteredOptions[0];

                selectors[nextIndex] = [
                    nextKey,
                    nextValue
                ];
            } else {
                break;
            }
        }

        return selectors.map((selector, index) => {
            const [ key ] = selector;
            return index > nextIndex ? [ key, '' ] : selector;
        });
    }

    setupAccessibility() {
        this.$ariaLive.attr('aria-live', 'polite');
    }

    sendProductViewDatalayer() {
        productDetailsDatalayer.sendProductDetailView(this.currencyCode, this.activeGAProduct);
    }

    setupHistoryState() {
        const urlParams = new URLSearchParams(this.activeSelectors);
        const queryStr = urlParams.toString();
       /*  if(queryStr) {
            const url = `${window.location.pathname}?${queryStr}`;
            window.history.replaceState(null, '', url);
            this.login?.setLink();
        } */
    }

    setupInitialSelectors() {
        const initialSelectors = this.getSelectorsFromUrl();

        this.uiSelectorOptions.forEach(option => {
            const values = option.Values || [];
            if(values.length === 1) {
                initialSelectors[option.Name] = values[0].FormattedValue;
            }
        });

        return initialSelectors;
    }

    setupListeners() {
        this.$selectorBoxes.on('change', this.onSelectorChange.bind(this));
        window.addEventListener('popstate', (evt) => this.onPopState(evt));
        $('#quickshop-modal').on('hidden.bs.modal', (evt) => this.sliderdestroy(evt));
    }

    setupSelectors() {
        let selectors = this.uiSelectorOptions.map(({ Name: name }) => {
            return [
                name,
                this.initialSelectors[name] || ''
            ];
        });

        const currentIndex = selectors.filter(([, value ]) => value).length;
        return this.resetSelectors(selectors, currentIndex);
    }

    toggleVideo() {
        this.$video.hide();
    }

    updatePrescription() {
        if(this.activeProduct.RxRequired) {
            this.$rx.slideDown('fast');
            this.$rxStamp.slideDown('fast');
        } else {
            this.$rx.slideUp('fast');
            this.$rxStamp.slideUp('fast');
        }
    }

    updateProduct() {
        this.filteredProducts = this.filterProducts(this.variants, this.selectors);
    }

    updatePromoLegend() {
        if((this.isVariant && !this.activeProduct?.PromotionModels) && (this.isVariant && !this.activeProduct?.ProductException == null)) {
            this.$promoLegend.empty();
        } else {
            this.renderPromoLegend();
        }
    }

    updatePrice() {
        this.$price.hide().empty();
        this.$listPrice.hide().empty();
        this.$discountPrice.hide().empty();

        if(this.isVariant) { 
            const listPrice = priceString(this.activeProduct.ListPrice);
            const exception = this.activeProduct.ProductException;

            if(exception && !exception.IsUserCareClub && exception.IsCareClub){
                this.$price.show().html(listPrice);
                return;
            }

            if(exception && typeof exception.PriceTreatment === 'number') {
                const discountPrice = priceString(exception.DiscountAmount);

                if(exception.PriceTreatment === exceptionBehaviorValues.STRIKE_THROUGH) {
                    this.$listPrice.show().html(discountPrice);
                    this.$discountPrice.show().html(listPrice);
                } else {
                    this.$price.show().html(discountPrice);
                }
            } else {
                this.$price.show().html(listPrice);
            }
        } else {
            const filteredPrices = this.filteredProducts.map(product => {
                //const discountPrice = product.ProductException?.DiscountAmount ?? null;
                //return typeof discountPrice === 'number' ? discountPrice : product.ListPrice;
                return product.ListPrice;
            });

            const priceLow = Math.min(...filteredPrices);
            const priceHigh = Math.max(...filteredPrices);
            const priceLowFormatted = priceString(priceLow);
            const priceHighFormatted = priceString(priceHigh);
            
            if(priceLow !== priceHigh) {
                this.$price.show().html(`${priceLowFormatted} - ${priceHighFormatted}`);
            } else {
                this.$price.show().html(priceLowFormatted);
            }
        }
    }

    updateRenderedOptions() {
        if(this.activeSelectors && Array.isArray(this.activeSelectors) && this.activeSelectors.length) {
            let filteredProducts = [ ...this.variants ];

            this.activeSelectors.forEach((option, index) => {
                let selectors = [ ...this.activeSelectors ];
                selectors.length = index;

                filteredProducts = this.filterProducts(filteredProducts, selectors);
                const options = this.filterOptions(index, filteredProducts);

                this.renderNextOption(index, options);
            });

            this.updateSelectorValues();
        }
    }

    updateState() {
        this.updateProduct();

        // update state based off of new active product
        this.updatePrescription();
        this.updatePrice();
        this.updateSelectors();
        this.updatePromoLegend();
        this.renderRadioInputs();

        if(this.#cachedProduct.VariantId !== this.activeProduct.VariantId) {
            this.updateSlider();
        }
    }

    updateSelectors(nextActiveIndex = this.activeSelectors.length) {
        this.updateRenderedOptions();

        if(!this.isVariant) {
            // render options for next active selector
            const filteredOptions = this.filterOptions(nextActiveIndex, this.filteredProducts);

            if(filteredOptions.length) {
                this.renderNextOption(nextActiveIndex, filteredOptions);
            }
        }

        this.updateSelectorValues();
    }

    updateSelectorValues() {
        // set all selectors initial value
        this.selectors.forEach((selector, index) => {
            const [,value] = selector;
            this.$selectorBoxes.eq(index).val(value);
        });
    }

    updateSlider() {
        if(this.slider) {
            this.slider.updateSlides(this.activeProduct.RxRequired, this.slides);
        }
    }

    sliderdestroy() {
        $('div[data-region="main"]').slick('unslick');
        $('div[data-region="nav"]').slick('unslick');
    }

}


$(() => {
	$(document).on('click', '[data-control="quickShopBtn"]', function(event) {
		event.preventDefault();
        const productId = $(this).data('product-id')
        bindQuickviewClick(productId);
    });
});

async function bindQuickviewClick(productId) {
        if(productId == null || productId == ''){
            console.log('stop');
        }
        const el = document.getElementById('pdpQuickview');
        const dataEl = document.getElementById('pdpSelectorData');
        const dataLabels = document.getElementById('pdpSelectorDataLabels');
        $('#quickshop-modal').modal('show');
        $('.js-quickshop-loader').show();
        $('#pdpQuickview').empty();

        let params = productId

        const { success, data, error }  = await catalogProduct.get(params);
        if(!success){
            console.log('failed', error);
            return;        }

        if(el) {
           // const data = JSON.parse(dataEl.innerHTML);
            const dataL = JSON.parse(dataLabels.innerHTML);
            $('.js-quickshop-loader').hide();
            console.log(data);
            if(data) {
                const $el = $(el);
                new PdpSelector({
                    $el,
                    dataL,
                    data
                });
            }
        }
        
    }